import { FC, useEffect } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import ConnectivityAnalyzerConfigureForm, {
	ConnectivityAnalyzerConfigureFormProps
} from '../../../../../components/ProjectResource/ConnectivityAnalyzerConfigureForm'
import LoadingSpinner from '../../../../../components/LoadingSpinner'
import useCreateNewProjectResource from '../../../../../hooks/mutate/useCreateNewProjectResource'
import useSingleProject from '../../../../../hooks/query/useSingleProject'
import useProjectResources from '../../../../../hooks/query/useProjectResources'
import { useNewConnAllEnrollmentFormStore } from '../../../../../stores/projects/projectResource/ca/newConnAlEnrollmentFormStore'
import {
	ProjectResourceApproval,
	ProjectResourceRequest
} from '../../../../../types/projectResource'
import CONSTANTS from '../../../../../constants'
import { useQueryClient } from '@tanstack/react-query'
import { useFlashbarStateStore } from '../../../../../stores/notifications/flashbarStateStore'

const ConnectivityAnalyzerEnroll: FC = () => {
	const { id: projectID } = useParams() as { id: string }

	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string

	const navigate = useNavigate()

	const location = useLocation()

	const {
		formValues,
		actions: { setFormValues, clearFormValues }
	} = useNewConnAllEnrollmentFormStore()

	const queryClient = useQueryClient()

	const { mutate: createProjectResouce, isLoading, error: createProjectResouceError } =
		useCreateNewProjectResource()

	const { addItem: addFlashbarItem, removeItem: removeFlashbarItem } = useFlashbarStateStore()

	const { data: project, isLoading: isProjectLoading } = useSingleProject(
		projectID,
		true
	)

	let projectResourceAWSProjectID = encodeURIComponent(`AWSPROJ#${projectID}`)

	const { data: resources, isLoading: resourcesLoading } = useProjectResources(
		projectResourceAWSProjectID,
		'CNCT_ANLZR',
		true
	)

	const {
		trigger,
		control,
		setValue,
		setError
	} = useForm({
		mode: 'onChange',
		reValidateMode: 'onBlur',
		defaultValues: formValues
	})

	useEffect(() => {
		setFormValues(
			'analysisFrequency',
			CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[0]
		)
		setValue('analysisFrequency', CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[0])

		setFormValues('viewerEmails', [])
		setValue('viewerEmails', [])

		console.log('FormValues useEffect#1', formValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (resources !== undefined && resources.length > 0) {
			switch (resources[0].details.analysisFrequency) {
				case 'daily':
					setFormValues(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[1]
					)
					setValue(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[1]
					)
					break
				case 'weekly':
					setFormValues(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[2]
					)
					setValue(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[2]
					)
					break
				case 'monthly':
					setFormValues(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[3]
					)
					setValue(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[3]
					)
					break
				case null:
					setFormValues(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[0]
					)
					setValue(
						'analysisFrequency',
						CONSTANTS.CA_ANALYSIS_FREQUENCY_OPTIONS[0]
					)
					break
			}
		}
	}, [resources, setFormValues, setValue])

	if (isProjectLoading || resourcesLoading) return <LoadingSpinner />

	if (!project || !resources) {
		return <h2>An Error Has Occured</h2>
	}

	let approvalStatus = {} as ProjectResourceApproval[]
	let tagDetails = {} as Record<any, any>

	if (project.tags !== undefined) {
		approvalStatus = project.tags.approvalStatus as ProjectResourceApproval[]
		tagDetails = project.tags.details
	}

	const resetForm = () => {
		clearFormValues()
		queryClient.resetQueries({queryKey: ['projectResources']})
		navigate('../../', {relative: 'path'})
	}

	const submitForm = () => {
		trigger()
		console.log('FormValues in Parent', formValues)
		let newProjectResource: ProjectResourceRequest = {
			status: 'PENDING_APPROVAL',
			requestorID: thisUserEmail,
			details: {
				viewers: formValues.viewerEmails.map(
					(viewer: { label: string }) => viewer.label
				),
				projectID: projectResourceAWSProjectID,
				projectName: project.projectName,
				platformName: tagDetails.platformName.value,
				analysisFrequency: formValues.analysisFrequency,
				systemAlias: tagDetails.systemAlias
			},
			action: 'CREATE',
			resourceType: 'CNCT_ANLZR',
			approvalStatus: approvalStatus
		}

		console.log(newProjectResource)

		createProjectResouce(newProjectResource, {
			onSuccess: () => {
				resetForm()
				addFlashbarItem({
					type: 'success',
					dismissible: true,
					dismissLabel: 'Dismiss success',
					onDismiss: () => removeFlashbarItem(`success:${location.pathname}`),
					id: `success:${location.pathname}`,
					content: `Sucessfully configured connectivity analyzer for ${tagDetails.platformName.value}/${project.projectName || ''}`,
				})
				addFlashbarItem({
  				type: 'info',
  				dismissible: true,
  				dismissLabel: 'Dismiss info',
  				onDismiss: () => removeFlashbarItem(`info:${location.pathname}`),
  				id: `info:${location.pathname}`,
  				header: `Information`,
  				content: 'Recent changes made might take a few moments to be reflected.'
				})
			},
			onError: () => {
				resetForm()
				addFlashbarItem({
					type: 'error',
					dismissible: true,
					dismissLabel: 'Dismiss error',
					onDismiss: () => removeFlashbarItem(`error:${location.pathname}`),
					id: `error:${location.pathname}`,
					header: `Failed to configure connectivity analyzer for ${tagDetails.platformName.value}/${project.projectName || ''}`,
					content: `An error has occured. ${createProjectResouceError}`
				})
			}
		})
	}

	let formProps: ConnectivityAnalyzerConfigureFormProps = {
		control: control,
		formValues: formValues,
		setFormValues: setFormValues,
		setError: setError,
		isLoading: isLoading,
		submitForm: submitForm,
		cancelForm: resetForm,
		platformName: tagDetails.platformName.value,
		projectName: project.projectName || ''
	}

	if (resources.length > 0) {
		formProps.existingViewers = resources[0].details.viewers
		formProps.existingAnalysisFrequency = resources[0].details.analysisFrequency
	}

	return <ConnectivityAnalyzerConfigureForm {...formProps} />
}

export default ConnectivityAnalyzerEnroll
